// TODO - Clean up - reduce complexity
/* eslint complexity: ["error", 30] */
import { ReactElement, ReactNode, useEffect, useState } from 'react';
import { Route, Routes, useNavigate } from 'react-router';
import PropTypes from 'prop-types';
import { useBrand } from '../../BrandContext';
import { useFormSubmit } from '../../data';
import TimedOut from '../../pages/TimedOut';
import { useAggregatorConsumerName } from '../../utilities/aggregatorConsumerNameProvider';
import { journeys, routes } from '../../utilities/constants';
import pushToDataLayer, { EVENTS } from '../../utilities/dataLayer';
import { setNewRelicAttribute, addNewRelicPageAction } from '../../utilities/newRelic';
import { SessionProvider } from '../../utilities/session';
import useQueryParams from '../../utilities/useQueryParams';
import useTechnicalError from '../../utilities/useTechnicalError';
import { isAggregatorConsumer, useSinglePage, parseCookie, normalizeFormValues } from '../../utilities/utils';
import useBackground from '../../utilities/useBackground';
import { getValue } from '../../utilities/formValuesMutation/formValuesMutation';
import Footer from '../Footer';
import { useFormData } from '../FormDataContext';
import Header from '../Header';
import Result from './Result';
import BaseMain from '../Main';
import QuotationHeroSection from '../QuotationHeroSection';
import LoadingSpinner from '../LoadingSpinner';
import { useAdobeExperiments } from '../../experiments/AdobeExperiment/AdobeExperimentProvider';
import { getFbString, getVcString } from '../../experiments/getExperimentString';
import { findMbox } from '../experiments/offers';
import QuickCheck from '../../pages/QuickCheck/QuickCheck';
import OpenBankingContingent from '../../pages/OpenBankingLanding/OpenBankingLanding';
import OpenBankingPending from '../../pages/OpenBankingPending/OpenBankingPending';
import { FormProvider, useForm } from 'react-hook-form';
import withExperiment from '../experiments/withExperiment';
import {
    OB_ACCEPT_MODAL_VARIANT,
    OB_ACCEPT_OPTED_IN_VARIANT
} from '../experiments/variants/OpenBankingAcceptRegionTest/variants';
import type { FormValues, QuotationSubmitRequest } from '../../types';

const ExperimentOpenBankingContingent = withExperiment(OpenBankingContingent, 'quotations-ob-landing-factorial-mbox');

export const destructureSParameter = (
    sParameter?: string
): { doubleClickId?: string; experimentVariant?: string; webpageChar?: string; vendorCode?: string } => {
    if (sParameter === undefined) return {};

    const doubleClickId = sParameter.substring(0, 8);
    const experimentVariant = /[A-Z]/.test(sParameter.charAt(1)) ? sParameter.charAt(1) : undefined;
    const webpageChar = /[A-Z]/.test(sParameter.charAt(5)) ? sParameter.charAt(5) : undefined;
    const vendorCode = /(?!(XXX))[A-Z1-9]{3}/.test(sParameter?.substring(8, 11) ?? '')
        ? sParameter?.substring(8, 11)
        : undefined;
    return { doubleClickId, experimentVariant, webpageChar, vendorCode };
};

const EditSpinner = ({
    formLoading,
    quotation,
    children
}: {
    formLoading?: boolean;
    quotation?: string;
    children: ReactNode;
}): ReactNode => {
    return formLoading && quotation ? <LoadingSpinner features={{ editSpinner: true }} /> : children;
};

const conditionalSetNewRelicAttribute = (
    attributeName: string,
    attributeValue: string | number | boolean | undefined
): void => {
    // set new relic attribute if that attribute is not undefined
    if (attributeValue) setNewRelicAttribute(attributeName, attributeValue);
};

const setNewRelicMboxAttribute = () => {
    if (parseCookie('mbox')) {
        setNewRelicAttribute('loadedWithTargetCookie', 'true');
    }
};

const Quotation = (): ReactElement | null => {
    const formDataContext = useFormData();
    const formData = formDataContext?.formData;
    const formLoading = formDataContext?.formLoading;
    const formResponse = formDataContext?.formResponse;
    const quotation = formDataContext?.quotation;
    const noFormDefinitionsConsumerName = formDataContext?.consumerName;
    const isEdit = formDataContext?.isEdit;
    // TODO have the `FormDataLoadingBlock` before this component so that we are certain it has been loaded
    // This is a limitation right now because a lot of the page can be rendered while we wait for the response
    // but that is deeply nested in the tree and needs to be abstracted so that it is rendered before we get here
    const sessionId = formData?.sessionId;
    const decisionDetails = formData?.decisionDetails;
    const consumerName = formData?.consumerName;
    const oemc = formData?.oemc;
    const quotationContext = formData?.requestDetails?.quotationContext;
    const referralCode = formData?.referralCode;
    const openBanking = formData?.openBanking;
    const [, setAggregatorConsumerName] = useAggregatorConsumerName();
    const navigate = useNavigate();
    const brand = useBrand();
    const rawInitialQueryParams = useQueryParams();
    const [initialQueryParam] = useState(rawInitialQueryParams);
    const [acceptRegionVariant, setAcceptRegionVariant] = useState('');

    const {
        campaign,
        productId,
        sParameter,
        channel,
        pcr,
        vendorCode: urlVendorCode,
        oemc: urlOEMC,
        referral
    } = initialQueryParam;
    const {
        doubleClickId,
        experimentVariant,
        webpageChar,
        vendorCode: sParamVendorCode
    } = destructureSParameter(sParameter);
    const vendorCode = formData?.vendorCode || sParamVendorCode || urlVendorCode || urlOEMC || 'CQC';
    const effectiveReferral = referralCode || referral;

    const background = useBackground('quotation');

    useEffect(() => setNewRelicMboxAttribute(), []);

    const adobeState = useAdobeExperiments();
    useEffect(() => {
        const experimentStringFB = getFbString(adobeState);
        const experimentStringVC = getVcString(adobeState);
        conditionalSetNewRelicAttribute('targetVariantsFB', experimentStringFB);
        conditionalSetNewRelicAttribute('targetVariantsVC', experimentStringVC);
        setAcceptRegionVariant(
            findMbox(adobeState.data, 'quotations-ob-accept-region-opt-in-mbox')?.content?.variant ?? ''
        );
    }, [adobeState]);

    useEffect(() => {
        conditionalSetNewRelicAttribute('brand', brand);
        conditionalSetNewRelicAttribute('oemc', oemc);
        conditionalSetNewRelicAttribute('vendorCode', vendorCode);
        conditionalSetNewRelicAttribute('productId', productId);
        conditionalSetNewRelicAttribute('experimentVariant', experimentVariant);
    }, [brand, oemc, vendorCode, productId, experimentVariant]);

    const journey = journeys.STANDARD; // Sort this out later

    useEffect(() => {
        if (sessionId && window?.newrelic?.setUserId != null) {
            window.newrelic.setUserId(sessionId);
        }
    }, [sessionId]);

    const [submitForm, submitting, { response, error: submitFormError }] = useFormSubmit();
    const methods = useForm({
        mode: 'all',
        reValidateMode: 'onChange',
        shouldFocusError: false,
        defaultValues: normalizeFormValues(formData?.formValues),
        criteriaMode: 'all'
    });

    useEffect(() => {
        if (formData?.formValues) {
            methods.reset(normalizeFormValues(formData.formValues));
        }
    }, [formData?.formValues]);

    useEffect(() => {
        if (submitting) {
            navigate(routes.result);
        }

        if (consumerName && isAggregatorConsumer(consumerName)) {
            setAggregatorConsumerName(consumerName);
        }
    }, [submitting, consumerName, setAggregatorConsumerName]);

    const isPartnerAggsEdit = useSinglePage(brand, consumerName);

    const error = submitFormError;

    useEffect(() => {
        if (response && effectiveReferral) {
            addNewRelicPageAction('Referral code used');
        }
    }, [response, effectiveReferral]);

    useTechnicalError(error);

    if (submitFormError) {
        pushToDataLayer({ event: EVENTS.SUBMISSION_RESULT, result: 'error' });
        return null;
    }

    const getCashAdvance = (values: FormValues): 'yes' | 'no' | 'unanswered' => {
        const cashAdvanceResponse = getValue('cash-advance', values);
        if (cashAdvanceResponse === 'Y') {
            return 'yes';
        }
        if (cashAdvanceResponse === 'N') {
            return 'no';
        }
        return 'unanswered';
    };

    const handleSubmit = (values: FormValues): void => {
        const openBankingOptedIn = getValue('openBankingOptedIn', values);
        const openBankingJourneyCandidate = openBankingOptedIn || acceptRegionVariant === OB_ACCEPT_MODAL_VARIANT;
        const formData: QuotationSubmitRequest = {
            formResponse,
            response: values,
            onlineEntryMethodCode: vendorCode,
            productId,
            sParameter,
            marketingOptIn: isPartnerAggsEdit ? 'false' : getValue('marketing-permission', values),
            requoteOptIn: isPartnerAggsEdit ? 'false' : getValue('future-soft-search-permission', values),
            cashAdvance: getCashAdvance(values),
            visitorId: window.Zuko?.getVisitorId?.(),
            preTaxIncomeDuration: 'yearly', // hoping to clear these up once we have permission to remove them from the dataflake
            otherIncomeDuration: 'yearly',
            quotationContext,
            ...(!!openBanking && { openBanking }),
            ...(noFormDefinitionsConsumerName && { consumerName: noFormDefinitionsConsumerName }),
            ...(!!decisionDetails && { decisionDetails }),
            ...(sessionId && { sessionId }),
            ...(openBankingJourneyCandidate && { openBankingJourneyCandidate })
        };

        if (effectiveReferral) {
            formData.referralCode = effectiveReferral;
        }
        submitForm(formData);
    };

    const openBankingOptedIn = Boolean(methods.getValues('openBankingOptedIn'));
    const effectiveAcceptRegionVariant =
        !openBankingOptedIn && acceptRegionVariant === OB_ACCEPT_OPTED_IN_VARIANT ? '' : acceptRegionVariant;

    return (
        <Routes>
            <Route
                path={routes.result}
                element={
                    <>
                        {/* @ts-expect-error TS2786: 'Result' cannot be used as a JSX component. */}
                        <Result
                            responseData={response?.data}
                            submitting={submitting}
                            pcr={pcr}
                            campaign={campaign}
                            channel={channel}
                            vendorCode={vendorCode}
                            acceptRegionVariant={effectiveAcceptRegionVariant}
                        />
                        <Footer />
                    </>
                }
            />

            <Route path="/ob-landing" element={<ExperimentOpenBankingContingent />} />
            <Route path="/ob-pending" element={<OpenBankingPending />} />

            <Route
                path="*"
                element={
                    <EditSpinner formLoading={formLoading} quotation={quotation}>
                        <SessionProvider {...{ sessionId, journey }}>
                            <TimedOut>
                                <Header mainPage={true} hideLogo={methods?.formState?.isDirty} />
                                <BaseMain bg={background} data-qa-id="quotation-form-page" zIndex="2">
                                    <FormProvider {...methods}>
                                        {/* TODO: can any of this be lazy loaded? */}
                                        <QuotationHeroSection
                                            isPartnerAggsEdit={isPartnerAggsEdit}
                                            // @ts-expect-error TS2769: No overload matches this call.
                                            isEdit={!!quotation}
                                        />
                                        <QuickCheck
                                            onSubmit={handleSubmit}
                                            quotation={quotation}
                                            monitoringParams={{
                                                onlineEntryMethodCode: vendorCode,
                                                consumerName: noFormDefinitionsConsumerName,
                                                journeyType: isEdit ? 'pre-pop' : 'standard',
                                                doubleClickId,
                                                experimentChar: experimentVariant,
                                                webpageChar,
                                                isEdit
                                            }}
                                        />
                                    </FormProvider>
                                </BaseMain>
                            </TimedOut>
                            <Footer />
                        </SessionProvider>
                    </EditSpinner>
                }
            />
        </Routes>
    );
};

EditSpinner.propTypes = { formLoading: PropTypes.bool, quotation: PropTypes.string, children: PropTypes.node };

export default Quotation;
